import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { IoMdClose } from 'react-icons/io';
import { Check2Circle, ExclamationTriangleFill } from 'react-bootstrap-icons';
import '../../../../components/StageChangeModal/StageChangeModal.css';
import './TransitionPreviewModal.css';
import { getAuthToken } from "../../../../utils/auth";

// Confirmation/amber-banner screen for the rule-driven stage transition
// engine, mirroring StageChangeModal's dialog structure but backed by
// preview-stage-transition / execute-stage-transition instead of the old
// allowed-stage-transitions / manual-stage-change pair.
const TransitionPreviewModal = ({ applicationId, stageTransitionId, scopedEntityId, onClose, onExecuted }) => {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [executing, setExecuting] = useState(false);
    const [reasonCategory, setReasonCategory] = useState('');
    const [reasonText, setReasonText] = useState('');

    const authToken = useMemo(() => {
        // const tokens = JSON.parse(sessionStorage.getItem('tokens')) || null;
        // return tokens?.token || null;
        return getAuthToken();
    }, []);

    const headers = { Authorization: `Bearer ${authToken}`, 'Content-type': 'application/json' };

    useEffect(() => {
        const fetchPreview = async () => {
            setLoading(true);
            try {
                const resp = await axios.get(
                    `${process.env.REACT_APP_API_BASE_URL}/applications/${applicationId}/preview-stage-transition`,
                    {
                        headers,
                        params: {
                            stage_transition_id: stageTransitionId,
                            scoped_entity_id: scopedEntityId,
                        },
                    }
                );
                setPreview(resp?.data?.data);
            } catch (error) {
                toast.error(error?.response?.data?.message || 'Failed to load transition preview');
                onClose();
            } finally {
                setLoading(false);
            }
        };

        fetchPreview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationId, stageTransitionId]);

    const handleExecute = async () => {
        if (preview?.requires_reason && !reasonCategory) {
            toast.error('Please select a reason category');
            return;
        }
        if (reasonCategory === 'Other' && reasonText.trim().length < 20) {
            toast.error('Reason text must be at least 20 characters when category is "Other"');
            return;
        }

        setExecuting(true);
        try {
            const resp = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/applications/execute-stage-transition`,
                {
                    application_id: applicationId,
                    stage_transition_id: stageTransitionId,
                    scoped_entity_id: scopedEntityId,
                    reason_category: reasonCategory || undefined,
                    reason_text: reasonText || undefined,
                },
                { headers }
            );

            toast.success('Stage transition executed successfully');
            onExecuted?.(resp?.data?.data);
            onClose();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to execute stage transition');
        } finally {
            setExecuting(false);
        }
    };

    return (
        <div className="stage-change-modal-overlay" onClick={onClose}>
            <div className="stage-change-modal-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="stage-change-modal-content">
                    <div className="stage-change-modal-header">
                        <div>
                            <h5 className="stage-change-modal-title">Confirm Stage Transition</h5>
                            <span className="stage-change-modal-subtitle">
                                Review what this transition will do before confirming
                            </span>
                        </div>
                        <button onClick={onClose} className="btn-close">
                            <IoMdClose size={24} />
                        </button>
                    </div>

                    <div className="stage-change-modal-body">
                        {loading ? (
                            <div className="preview-loading">Loading preview...</div>
                        ) : (
                            <>
                                <div className="application-info">
                                    Application <strong>{applicationId}</strong>
                                </div>

                                <div className="stage-transition">
                                    <span className="stage-pill from">From: {preview?.from}</span>
                                    <span className="arrow">→</span>
                                    <span className="stage-pill to">To: {preview?.to}</span>
                                </div>

                                {preview?.hard_lock_warning && (
                                    <div className="amber-banner">
                                        <ExclamationTriangleFill /> {preview.hard_lock_warning}
                                    </div>
                                )}

                                {preview?.requires_two_person_rule && (
                                    <div className="amber-banner">
                                        <ExclamationTriangleFill /> Two-person rule applies — you cannot confirm this if you were the
                                        last officer to act on this application's stage.
                                    </div>
                                )}

                                {preview?.will_invalidate?.length > 0 && (
                                    <div className="prereq-box invalidate-box">
                                        <h6>Data that will be invalidated (flagged, never deleted)</h6>
                                        <ul>
                                            {preview.will_invalidate.map((model) => (
                                                <li key={model}>
                                                    <span className="check-icon">
                                                        <Check2Circle />
                                                    </span>
                                                    {model}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {preview?.pending_reconfirmation?.length > 0 && (
                                    <div className="amber-banner">
                                        <ExclamationTriangleFill /> Rolled-back sections still need re-confirmation before
                                        this forward move makes sense:{' '}
                                        {preview.pending_reconfirmation.map((p) => p.model).join(', ')}.
                                    </div>
                                )}

                                {!preview?.gates_passed && (
                                    <div className="amber-banner error-banner">
                                        <ExclamationTriangleFill /> Gate check failed — this transition cannot be executed right now.
                                        {preview?.gate_results
                                            ?.filter((g) => !g.passed)
                                            ?.map((g, i) => (
                                                <div key={i} className="gate-fail-reason">
                                                    {g.check_code}: {g.reason}
                                                </div>
                                            ))}
                                    </div>
                                )}

                                {preview?.requires_reason && (
                                    <div className="reason-section">
                                        <label>Reason category</label>
                                        <select
                                            className="form-select mb-2"
                                            value={reasonCategory}
                                            onChange={(e) => setReasonCategory(e.target.value)}
                                        >
                                            <option value="">Select a reason...</option>
                                            {preview.reason_categories?.map((cat) => (
                                                <option key={cat} value={cat}>
                                                    {cat}
                                                </option>
                                            ))}
                                        </select>

                                        {reasonCategory === 'Other' && (
                                            <textarea
                                                value={reasonText}
                                                onChange={(e) => setReasonText(e.target.value)}
                                                placeholder="Enter reason (minimum 20 characters)..."
                                            />
                                        )}
                                    </div>
                                )}

                                {!preview?.requires_reason && (
                                    <div className="reason-section">
                                        <label>Note (optional)</label>
                                        <textarea
                                            value={reasonText}
                                            onChange={(e) => setReasonText(e.target.value)}
                                            placeholder="Optional note for this transition..."
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="stage-change-modal-footer">
                        <button onClick={onClose} className="stage-change-btn btn-outline">
                            Cancel
                        </button>
                        <button
                            onClick={handleExecute}
                            className="stage-change-btn btn-solid"
                            disabled={loading || executing || !preview?.gates_passed}
                        >
                            {executing ? 'Executing...' : 'Confirm & Execute'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TransitionPreviewModal;
